/**
 * F45 (Wave 2D) — engineering-authored day-of-week × hour-of-day activity
 * heatmap. `Chart` (@bravemove/ui-web) has line/bar/area/donut/funnel but no heatmap
 * kind, so this is a small CSS-grid composition: 7 rows (dow 0=Sun..6=Sat) ×
 * 24 columns (hour 0..23), each cell shaded by booking count via an opacity ramp
 * over the accent token. Asia/Beirut day (matches the rollup writer).
 *
 * TODO(F45 design-polish): promote to a @bravemove/ui-web Heatmap primitive (meta-plan 2E).
 */
import { useMemo } from 'react';
import type { AnalyticsHeatmapCell } from '@bravemove/types';
import { DOW_ABBREV } from '../analyticsHelpers';

export interface ActivityHeatmapProps {
  readonly cells: readonly AnalyticsHeatmapCell[];
  readonly ariaLabel?: string;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

export function ActivityHeatmap({ cells, ariaLabel }: ActivityHeatmapProps) {
  const { lookup, max } = useMemo(() => {
    const map = new Map<string, number>();
    let m = 0;
    for (const c of cells) {
      map.set(`${c.dow}:${c.hour}`, c.count);
      if (c.count > m) m = c.count;
    }
    return { lookup: map, max: m };
  }, [cells]);

  return (
    <div
      className="overflow-x-auto"
      data-testid="analytics-activity-heatmap"
      role="img"
      aria-label={ariaLabel}
    >
      <div
        className="inline-grid gap-[2px]"
        style={{ gridTemplateColumns: `2.5rem repeat(24, 0.85rem)` }}
      >
        {/* header row: hour labels (every 3h to stay readable) */}
        <div aria-hidden="true" />
        {HOURS.map((h) => (
          <div
            key={`h-${h}`}
            aria-hidden="true"
            className="text-center font-mono text-[8px] leading-none text-ink-3"
          >
            {h % 3 === 0 ? h : ''}
          </div>
        ))}
        {DOW_ABBREV.map((label, dow) => (
          <Row key={`row-${dow}`} dow={dow} label={label} lookup={lookup} max={max} />
        ))}
      </div>
    </div>
  );
}

function Row({
  dow,
  label,
  lookup,
  max,
}: {
  dow: number;
  label: string;
  lookup: Map<string, number>;
  max: number;
}) {
  return (
    <>
      <div className="pe-1 text-end font-mono text-[9px] leading-[0.85rem] text-ink-3">{label}</div>
      {HOURS.map((hour) => {
        const count = lookup.get(`${dow}:${hour}`) ?? 0;
        const intensity = max === 0 ? 0 : count / max;
        // Opacity ramp on the accent token. Min visible floor so empty cells read as the grid.
        const opacity = count === 0 ? 0.06 : 0.18 + intensity * 0.82;
        return (
          <div
            key={`${dow}-${hour}`}
            className="h-[0.85rem] w-[0.85rem] rounded-[2px] bg-accent"
            style={{ opacity }}
            title={`${label} ${hour}:00 — ${count}`}
            data-dow={dow}
            data-hour={hour}
          />
        );
      })}
    </>
  );
}
